import { onboardingLeadStepDataSchema } from '@repo/shared-types';

function pick<T extends Record<string, unknown>>(source: unknown, keys: (keyof T & string)[]): Partial<T> | undefined {
  if (source == null || typeof source !== 'object' || Array.isArray(source)) return undefined;
  const o = source as Record<string, unknown>;
  const out: Partial<T> = {};
  for (const k of keys) {
    if (k in o && o[k] !== undefined) (out as Record<string, unknown>)[k] = o[k];
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Maps full wizard `stepData` to the strict public lead payload for POST /onboarding/signups.
 * Strips unknown and sensitive-step data before wire send.
 */
export function toOnboardingLeadStepData(raw: Record<string, unknown> | undefined) {
  if (!raw || typeof raw !== 'object') return onboardingLeadStepDataSchema.parse({});

  const built: Record<string, unknown> = {};

  const personal = pick(raw.personal, [
    'firstName',
    'lastName',
    'email',
    'phone',
    'jobTitle',
    'companyName',
    'dateOfBirth',
  ]);
  if (personal) built.personal = personal;

  const entity = pick(raw.entity, [
    'firstName',
    'lastName',
    'email',
    'phone',
    'investorType',
    'entityName',
    'title',
    'address',
    'city',
    'state',
    'zip',
  ]);
  if (entity) built.entity = entity;

  const company = pick(raw.company, [
    'companyName',
    'phone',
    'address',
    'city',
    'state',
    'zip',
    'dba',
    'teamSize',
    'website',
  ]);
  if (company) built.company = company;

  const license = pick(raw.license, ['licenseNumber', 'licenseState', 'licenseExpiration', 'verifyLater']);
  if (license) built.license = license;

  const property = pick(raw.property, ['addPropertiesNow']);
  if (property) built.property = property;

  const pma = pick(raw['property-management-approach'], [
    'managementStructure',
    'inviteCode',
    'searchPM',
    'linkMethod',
  ]);
  if (pma) built['property-management-approach'] = pma;

  const ownerProfile = pick(raw['owner-profile'], [
    'firstName',
    'lastName',
    'email',
    'phone',
    'ownershipType',
    'entityName',
  ]);
  if (ownerProfile) built['owner-profile'] = ownerProfile;

  const wf = pick(raw['workflow-preferences'], ['monthlyReports', 'maintenanceAlerts']);
  if (wf) built['workflow-preferences'] = wf;

  const lease = pick(raw.lease, ['applicationId', 'acknowledge']);
  if (lease) built.lease = lease;

  const emergency = pick(raw.emergency, ['emergencyContactName', 'emergencyContactPhone', 'emergencyContactRelation']);
  if (emergency) built.emergency = emergency;

  if (typeof raw.applicationId === 'string' && raw.applicationId.trim()) {
    built.applicationId = raw.applicationId.trim();
  }

  const business = pick(raw.business, [
    'businessName',
    'contactFirstName',
    'contactLastName',
    'email',
    'phone',
  ]);
  if (business) built.business = business;

  const terms = pick(raw.terms, ['agreedToTerms', 'offeringCode', 'investmentAmount', 'investmentType']);
  if (terms) built.terms = terms;

  const accreditation = pick(raw.accreditation, ['status', 'accreditationType', 'notes']);
  if (accreditation) built.accreditation = accreditation;

  const services = pick(raw.services, ['serviceLevel']);
  if (services) built.services = services;

  if (raw.welcome != null && typeof raw.welcome === 'object' && !Array.isArray(raw.welcome) && Object.keys(raw.welcome).length === 0) {
    built.welcome = {};
  }

  return onboardingLeadStepDataSchema.parse(built);
}
